import React from "react";
import { Modal } from "react-bootstrap";

const PERIOD_LABELS = {
  onetime: "einmalig",
  daily: "pro Tag",
  monthly: "pro Monat",
  yearly: "pro Jahr",
};

const formatAmount = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "–";
  return amount.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const TariffComparisonModal = ({
  show,
  onHide,
  offers = [],
  selectedBatchId,
  onSelectOffer,
  billingPeriod = "yearly",
  setBillingPeriod,
}) => {
  return (
    <Modal show={show} onHide={onHide} size="xl" centered className="myn-comparison-modal">
      <Modal.Header closeButton className="border-0 pb-0">
        <div>
          <Modal.Title className="h4 mb-1">Tarif- &amp; Angebotsvergleich</Modal.Title>
          <p className="text-muted mb-0 small">
            Vergleichen Sie alle verfügbaren Angebote für Ihr Unternehmen und wählen Sie die passende Zahlweise.
          </p>
        </div>
      </Modal.Header>

      <Modal.Body className="pt-3">
        {/* Zahlweise Umschalter */}
        <div className="d-flex justify-content-center mb-4">
          <div className="btn-group p-1 bg-light rounded-pill border" role="group" aria-label="Zahlungsintervall">
            <button
              type="button"
              className={`btn btn-sm rounded-pill px-4 ${billingPeriod === "monthly" ? "btn-primary shadow-sm" : "btn-light"}`}
              onClick={() => setBillingPeriod && setBillingPeriod("monthly")}
            >
              Monatliche Zahlung
            </button>
            <button
              type="button"
              className={`btn btn-sm rounded-pill px-4 ${billingPeriod === "yearly" ? "btn-success shadow-sm" : "btn-light"}`}
              onClick={() => setBillingPeriod && setBillingPeriod("yearly")}
            >
              Jährliche Vorauszahlung <span className="badge bg-warning text-dark ms-1">20% Rabatt</span>
            </button>
          </div>
        </div>

        {/* Angebotskarten im Vergleich */}
        <div className="row g-3 justify-content-center">
          {offers.map((offer) => {
            const isSelected = String(offer.batch_id) === String(selectedBatchId);
            const rawMonthly = Number(offer.tariff.amount_eur_net) || 0;
            const discountPercent = Number(offer.tariff.yearly_discount_percent) || (offer.tariff.amount_period_net === "monthly" ? 20 : 0);
            const fee = Number(offer.tariff.activation_fee_eur_net) || 0;
            const soldOut = Number(offer.available) <= 0;

            // Preisberechnung
            let displayPriceMonthly = rawMonthly;
            let displayPriceYearly = rawMonthly * 12;
            let savingsYearly = 0;

            if (offer.tariff.amount_period_net === "monthly") {
              const fullYear = rawMonthly * 12;
              savingsYearly = fullYear * (discountPercent / 100);
              displayPriceYearly = fullYear - savingsYearly;
              displayPriceMonthly = displayPriceYearly / 12;
            } else if (offer.tariff.amount_period_net === "yearly") {
              savingsYearly = rawMonthly * (discountPercent / 100);
              displayPriceYearly = rawMonthly - savingsYearly;
              displayPriceMonthly = displayPriceYearly / 12;
            }

            const colClass = offers.length === 1 
              ? "col-12 col-md-8 col-lg-6" 
              : offers.length === 2 
              ? "col-12 col-md-6" 
              : "col-12 col-md-6 col-lg-4";

            return (
              <div key={offer.batch_id} className={colClass}>
                <div
                  className={`card h-100 shadow-sm border-2 ${
                    isSelected ? "border-primary bg-light-subtle" : "border-light-subtle"
                  }`}
                  style={{ borderRadius: "16px", overflow: "hidden" }}
                >
                  {isSelected && (
                    <div className="bg-primary text-white text-center py-1 small fw-bold">
                      Aktuell ausgewählt
                    </div>
                  )}

                  <div className="card-body p-4 d-flex flex-column">
                    <div className="mb-3">
                      <span className="badge bg-primary-subtle text-primary mb-2">
                        {offer.service.service_name}
                      </span>
                      <h4 className="card-title h5 mb-1">{offer.batch_name}</h4>
                      <p className="text-muted small mb-0">Tarif: {offer.tariff.tariff_name}</p>
                    </div>

                    {/* Preisbox */}
                    <div className="my-3 p-3 bg-white rounded border">
                      {billingPeriod === "yearly" ? (
                        <>
                          <div className="d-flex flex-wrap align-items-baseline gap-1">
                            <span className="h3 mb-0 text-success fw-bold text-nowrap">
                              {formatAmount(displayPriceMonthly)} €
                            </span>
                            <span className="text-muted small">netto / Monat</span>
                          </div>
                          <div className="small text-muted mt-1">
                            <span className="text-nowrap">{formatAmount(displayPriceYearly)} € netto / Jahr</span>
                            {discountPercent > 0 && (
                              <span className="badge bg-success-subtle text-success ms-2">
                                -{discountPercent}%
                              </span>
                            )}
                          </div>
                          {savingsYearly > 0 && (
                            <div className="text-success small fw-semibold mt-1">
                              Sie sparen {formatAmount(savingsYearly)} € im ersten Jahr!
                            </div>
                          )}
                        </>
                      ) : (
                        <>
                          <div className="d-flex flex-wrap align-items-baseline gap-1">
                            <span className="h3 mb-0 text-dark fw-bold text-nowrap">
                              {formatAmount(rawMonthly)} €
                            </span>
                            <span className="text-muted small">
                              netto {PERIOD_LABELS[offer.tariff.amount_period_net] || "pro Monat"}
                            </span>
                          </div>
                          {discountPercent > 0 && (
                            <div className="text-muted small mt-1">
                              Mit Jahresvorauszahlung nur {formatAmount(displayPriceMonthly)} €/Monat
                            </div>
                          )}
                        </>
                      )}

                      {fee > 0 && (
                        <div className="text-muted small mt-2 pt-2 border-top">
                          zzgl. {formatAmount(fee)} € Aktivierungsgebühr
                        </div>
                      )}
                    </div>

                    {/* Leistungsmerkmale */}
                    <ul className="list-unstyled small mb-4 flex-grow-1">
                      <li className="mb-2 d-flex align-items-center">
                        <span className="text-success me-2">✔</span> Direkter QR-Code zur Google-Bewertungsseite
                      </li>
                      <li className="mb-2 d-flex align-items-center">
                        <span className="text-success me-2">✔</span> Schnelle Weiterleitung ohne Google-Umwege
                      </li>
                      <li className="mb-2 d-flex align-items-center">
                        <span className="text-success me-2">✔</span> Kein App-Download für Ihre Kunden erforderlich
                      </li>
                      <li className="mb-2 d-flex align-items-center">
                        <span className="text-success me-2">✔</span> QR-Code für WhatsApp, Telegram &amp; Flyer-Download
                      </li>
                      <li className="mb-2 d-flex align-items-center">
                        <span className="text-success me-2">✔</span> DSGVO-konforme Datenverarbeitung in Deutschland
                      </li>
                      <li className="mb-2 d-flex align-items-center">
                        <span className="text-success me-2">✔</span>
                        {soldOut ? (
                          <span className="text-danger fw-bold">Derzeit vergriffen</span>
                        ) : (
                          <span>Noch {offer.available} Kontingente verfügbar</span>
                        )}
                      </li>
                    </ul>

                    {/* Auswahl-Schaltfläche */}
                    <button
                      type="button"
                      disabled={soldOut}
                      className={`btn w-100 py-2 ${
                        isSelected ? "btn-outline-primary fw-bold" : "btn-primary"
                      }`}
                      onClick={() => {
                        onSelectOffer(offer.batch_id, billingPeriod);
                        onHide();
                      }}
                    >
                      {soldOut
                        ? "Vergriffen"
                        : isSelected
                        ? "✓ Gewählt – übernehmen"
                        : "Diesen Tarif wählen"}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </Modal.Body>

      <Modal.Footer className="border-0 pt-0">
        <button type="button" className="btn btn-secondary px-4" onClick={onHide}>
          Schließen
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default TariffComparisonModal;
